/**
 * Forms Plugin
 *
 * Auto-discovers and renders all published lead magnet forms for the current
 * site. Follows the Klaviyo model: one sdk.js, one write key, forms render
 * automatically with no embed codes.
 *
 * Flow:
 *   1. Read writeKey + host from analytics instance
 *   2. Fetch GET {host}/api/v1/public/forms?writeKey=...&siteUrl=...
 *      (stale-while-revalidate from sessionStorage)
 *   3. For each form:
 *      a. shouldShow() — device/URL/frequency gate
 *      b. waitForTrigger() — async, resolves on delay/scroll/exit/click
 *      c. createDisplayContainer() — Popup or Inline
 *      d. renderForm() — Shadow DOM render
 *      e. markShown() — frequency state
 *   4. Scan DOM for <div data-sf-form-id="..."> placeholders and render
 *      inline forms into them (fetched individually by websiteFormId).
 *
 * Registered in cdn-entry.ts alongside UTM, Shopify, WooCommerce plugins.
 */

import type { Plugin } from "./plugin.js";
import type { SegmentflowWeb } from "../web.js";
import { shouldShow, waitForTrigger, markShown, type DisplayRules } from "./forms/display-rules.js";
import { createDisplayContainer } from "./forms/display-container.js";
import { renderForm, type PublicFormData } from "./forms/form-renderer.js";

// ============================================================================
// Cache
// ============================================================================

const CACHE_KEY = "sf_forms_config";
const CACHE_TTL_MS = 60_000; // 1 minute — matches API Cache-Control: max-age=60

interface CachedFormsConfig {
  ts: number;
  forms: PublicFormData[];
}

// ============================================================================
// Plugin factory
// ============================================================================

export function createFormsPlugin(): Plugin {
  return {
    name: "forms",

    async load(analytics: SegmentflowWeb): Promise<void> {
      const writeKey = analytics.writeKey;
      const host = analytics.host;
      const siteUrl = window.location.origin;

      // ── Fetch published forms (stale-while-revalidate) ───────────────────
      let forms: PublicFormData[] = [];
      try {
        let cached: CachedFormsConfig | null = null;

        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          try {
            cached = JSON.parse(raw) as CachedFormsConfig;
          } catch {
            // Corrupted cache entry — ignore
          }
        }

        const isFresh = cached && Date.now() - cached.ts < CACHE_TTL_MS;
        if (isFresh && cached) {
          // Serve from cache — no network request
          forms = cached.forms;
        } else {
          // Serve stale immediately if available, then revalidate in background
          if (cached) {
            forms = cached.forms;
          }

          const url =
            `${host}/api/v1/public/forms` +
            `?writeKey=${encodeURIComponent(writeKey)}` +
            `&siteUrl=${encodeURIComponent(siteUrl)}`;

          const res = await fetch(url);
          if (res.ok) {
            const data = (await res.json()) as { forms: PublicFormData[] };
            forms = data.forms;
            sessionStorage.setItem(
              CACHE_KEY,
              JSON.stringify({ ts: Date.now(), forms } satisfies CachedFormsConfig),
            );
          }
        }
      } catch (err) {
        console.warn("[Segmentflow Forms] Failed to fetch forms", err);
        return;
      }

      // The SDK may be loaded in <head> (e.g. WordPress enqueues in header
      // by default), which means neither <body> nor inline placeholders
      // exist when this runs. Defer all rendering until DOM is ready so
      // `document.body.appendChild` (popup) and `querySelectorAll` (inline)
      // both see a fully-parsed document.
      const renderAll = () => {
        // ── Render popup forms ────────────────────────────────────────────
        for (const form of forms) {
          if (form.displayType === "Inline") continue; // handled below

          // Parse display rules with defaults
          const rules: Required<Pick<DisplayRules, "trigger" | "frequency" | "showOnMobile" | "showOnDesktop">> & DisplayRules = {
            trigger: "immediate",
            frequency: "once",
            showOnMobile: true,
            showOnDesktop: true,
            ...(form.displayRules as DisplayRules | undefined ?? {}),
          };

          // Synchronous gate: device type, URL patterns, frequency capping
          if (!shouldShow(rules, form.id)) continue;

          // Each form manages its own trigger independently (fire-and-forget)
          void (async () => {
            try {
              await waitForTrigger(rules);

              const container = createDisplayContainer({
                displayType: form.displayType,
                showCloseButton: rules.showCloseButton ?? true,
              });
              await renderForm(container.host, form, host, analytics);
              markShown(form.id, rules.frequency);
            } catch (err) {
              console.warn(
                `[Segmentflow Forms] Error rendering form ${form.id}`,
                err,
              );
            }
          })();
        }

        // ── Render inline forms into data-sf-form-id placeholders ────────
        //
        // Any element with `data-sf-form-id` is treated as an embed target.
        // The attribute value is the websiteFormId. The form config is
        // fetched individually (or matched from the already-fetched list).
        const placeholders = document.querySelectorAll<HTMLElement>("[data-sf-form-id]");
        for (const el of placeholders) {
          const websiteFormId = el.getAttribute("data-sf-form-id");
          if (!websiteFormId) continue;
          // Prevent double-rendering if the SDK re-initialises
          if (el.hasAttribute("data-sf-rendered")) continue;

          void (async () => {
            try {
              // Try to match from already-fetched forms list first
              let formData = forms.find((f) => f.websiteFormId === websiteFormId);

              if (!formData) {
                // Fetch individually — supports forms not in the auto-discover list
                const res = await fetch(`${host}/api/v1/public/forms/${encodeURIComponent(websiteFormId)}`);
                if (!res.ok) {
                  console.warn(`[Segmentflow Forms] Failed to fetch inline form ${websiteFormId}`);
                  return;
                }
                formData = (await res.json()) as PublicFormData;
              }

              const container = createDisplayContainer({
                displayType: "Inline",
                targetElement: el,
              });
              await renderForm(container.host, formData, host, analytics);
              el.setAttribute("data-sf-rendered", "true");
            } catch (err) {
              console.warn(
                `[Segmentflow Forms] Error rendering inline form ${websiteFormId}`,
                err,
              );
            }
          })();
        }
      };

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", renderAll, { once: true });
      } else {
        renderAll();
      }
    },
  };
}
